import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  FileText, 
  Calendar, 
  User,
  CheckCircle,
  XCircle,
  Bell,
  Settings,
  Filter,
  ArrowRight
} from 'lucide-react';
import { format, differenceInDays, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import type { Project } from '../../types';

interface ProjectAlert {
  id: string;
  project_id: string;
  project_name: string;
  owner_name: string;
  type: 'OVERDUE' | 'DEADLINE_APPROACHING' | 'NO_ACTIVITY' | 'MISSING_DOCUMENTS' | 'MEETING_NEEDED' | 'STATUS_STUCK';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  action_required: string;
  due_date?: string;
  days_overdue?: number;
  created_at: string;
}

interface AlertSettings {
  deadline_warning_days: number;
  inactivity_warning_days: number;
  enable_email_alerts: boolean;
  enable_push_notifications: boolean;
  alert_frequency: 'immediate' | 'daily' | 'weekly';
}

export const ProjectAlerts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { projects, fetchProjects } = useProjectStore();
  const [alerts, setAlerts] = useState<ProjectAlert[]>([]);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<AlertSettings>({
    deadline_warning_days: 7,
    inactivity_warning_days: 14,
    enable_email_alerts: true,
    enable_push_notifications: true,
    alert_frequency: 'daily'
  });

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    generateAlerts();
  }, [projects, settings]);

  const generateAlerts = () => {
    const generatedAlerts: ProjectAlert[] = [];
    const now = new Date();

    projects.forEach(project => {
      // Check for overdue projects
      if (project.end_date) {
        const endDate = new Date(project.end_date);
        const daysOverdue = differenceInDays(now, endDate);
        
        if (daysOverdue > 0 && !['TERMINE', 'REJETE', 'SUSPENDU'].includes(project.status)) {
          generatedAlerts.push({
            id: `overdue-${project.id}`,
            project_id: project.id,
            project_name: project.name,
            owner_name: project.owner?.full_name || 'Porteur',
            type: 'OVERDUE',
            severity: daysOverdue > 30 ? 'critical' : daysOverdue > 14 ? 'high' : 'medium',
            title: 'Projet en retard',
            description: `Le projet est en retard de ${daysOverdue} jour(s)`,
            action_required: 'Contacter le porteur et replanifier',
            due_date: project.end_date,
            days_overdue: daysOverdue,
            created_at: now.toISOString()
          });
        }
      }

      // Check for approaching deadlines
      if (project.end_date) {
        const endDate = new Date(project.end_date);
        const daysUntilDeadline = differenceInDays(endDate, now);
        
        if (daysUntilDeadline <= settings.deadline_warning_days && daysUntilDeadline > 0) {
          generatedAlerts.push({
            id: `deadline-${project.id}`,
            project_id: project.id,
            project_name: project.name,
            owner_name: project.owner?.full_name || 'Porteur',
            type: 'DEADLINE_APPROACHING',
            severity: daysUntilDeadline <= 3 ? 'high' : 'medium',
            title: 'Échéance proche',
            description: `Échéance dans ${daysUntilDeadline} jour(s)`,
            action_required: 'Vérifier l\'avancement et planifier si nécessaire',
            due_date: project.end_date,
            created_at: now.toISOString()
          });
        }
      }

      // Check for projects without recent activity
      const lastUpdate = new Date(project.updated_at);
      const daysSinceUpdate = differenceInDays(now, lastUpdate);
      
      if (daysSinceUpdate > settings.inactivity_warning_days && !['TERMINE', 'REJETE', 'SUSPENDU'].includes(project.status)) {
        generatedAlerts.push({
          id: `inactive-${project.id}`,
          project_id: project.id,
          project_name: project.name,
          owner_name: project.owner?.full_name || 'Porteur',
          type: 'NO_ACTIVITY',
          severity: daysSinceUpdate > 30 ? 'high' : 'medium',
          title: 'Aucune activité récente',
          description: `Pas de mise à jour depuis ${daysSinceUpdate} jour(s)`,
          action_required: 'Prendre contact avec le porteur',
          created_at: now.toISOString()
        });
      }

      // Check for missing documents
      const documentsCount = project.documents?.length || 0;
      if (documentsCount < 3 && ['SOUMIS', 'ACCORD_PROVISOIRE'].includes(project.status)) {
        generatedAlerts.push({
          id: `docs-${project.id}`,
          project_id: project.id,
          project_name: project.name,
          owner_name: project.owner?.full_name || 'Porteur',
          type: 'MISSING_DOCUMENTS',
          severity: 'medium',
          title: 'Documents manquants',
          description: `Seulement ${documentsCount} document(s) déposé(s)`,
          action_required: 'Relancer le porteur pour les documents',
          created_at: now.toISOString()
        });
      }

      // Check for projects needing meetings
      const hasRecentMeeting = false; // TODO: Check actual meetings
      if (!hasRecentMeeting && ['EN_COURS', 'VALIDE'].includes(project.status)) {
        generatedAlerts.push({
          id: `meeting-${project.id}`,
          project_id: project.id,
          project_name: project.name,
          owner_name: project.owner?.full_name || 'Porteur',
          type: 'MEETING_NEEDED',
          severity: 'low',
          title: 'Rendez-vous recommandé',
          description: 'Aucun RDV récent planifié',
          action_required: 'Planifier un point de suivi',
          created_at: now.toISOString()
        });
      }
    });

    setAlerts(generatedAlerts);
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesSeverity = !severityFilter || alert.severity === severityFilter;
    const matchesType = !typeFilter || alert.type === typeFilter;
    return matchesSeverity && matchesType;
  });

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'OVERDUE': return <Clock className="w-5 h-5" />;
      case 'DEADLINE_APPROACHING': return <Calendar className="w-5 h-5" />;
      case 'NO_ACTIVITY': return <User className="w-5 h-5" />;
      case 'MISSING_DOCUMENTS': return <FileText className="w-5 h-5" />;
      case 'MEETING_NEEDED': return <Calendar className="w-5 h-5" />;
      case 'STATUS_STUCK': return <AlertTriangle className="w-5 h-5" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'critical': return 'Critique';
      case 'high': return 'Élevé';
      case 'medium': return 'Moyen';
      case 'low': return 'Faible';
      default: return 'Non défini';
    }
  };

  const getTypeLabel = (type: string) => {
    const labels = {
      'OVERDUE': 'En retard',
      'DEADLINE_APPROACHING': 'Échéance proche',
      'NO_ACTIVITY': 'Inactif',
      'MISSING_DOCUMENTS': 'Documents manquants',
      'MEETING_NEEDED': 'RDV nécessaire',
      'STATUS_STUCK': 'Statut bloqué'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const handleAlertAction = (alert: ProjectAlert) => {
    // Navigate to project or take specific action
    navigate(`/projects/${alert.project_id}`);
  };

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const getAlertStats = () => {
    return {
      total: alerts.length,
      critical: alerts.filter(a => a.severity === 'critical').length,
      high: alerts.filter(a => a.severity === 'high').length,
      medium: alerts.filter(a => a.severity === 'medium').length,
      low: alerts.filter(a => a.severity === 'low').length
    };
  };

  const alertStats = getAlertStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertes Projets</h1>
          <p className="text-gray-600 mt-1">
            Surveillance proactive de vos projets en accompagnement
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>Paramètres</span>
          </button>
          <button
            onClick={generateAlerts}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Bell className="w-4 h-4" />
            <span>Actualiser</span>
          </button>
        </div>
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{alertStats.total}</p>
            <p className="text-sm text-gray-600">Total alertes</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-red-200 p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{alertStats.critical}</p>
            <p className="text-sm text-red-700">Critiques</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-orange-200 p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">{alertStats.high}</p>
            <p className="text-sm text-orange-700">Élevées</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-yellow-200 p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{alertStats.medium}</p>
            <p className="text-sm text-yellow-700">Moyennes</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-blue-200 p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{alertStats.low}</p>
            <p className="text-sm text-blue-700">Faibles</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Toutes les priorités</option>
            <option value="critical">Critique</option>
            <option value="high">Élevée</option>
            <option value="medium">Moyenne</option>
            <option value="low">Faible</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les types</option>
            <option value="OVERDUE">En retard</option>
            <option value="DEADLINE_APPROACHING">Échéance proche</option>
            <option value="NO_ACTIVITY">Inactif</option>
            <option value="MISSING_DOCUMENTS">Documents manquants</option>
            <option value="MEETING_NEEDED">RDV nécessaire</option>
            <option value="STATUS_STUCK">Statut bloqué</option>
          </select>

          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Filter className="w-4 h-4" />
            <span>{filteredAlerts.length} alerte{filteredAlerts.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune alerte</h3>
            <p className="text-gray-500">
              {severityFilter || typeFilter 
                ? 'Aucune alerte ne correspond aux filtres sélectionnés.'
                : 'Tous vos projets sont à jour ! Excellent travail.'
              }
            </p>
          </div>
        ) : (
          filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`bg-white rounded-xl shadow-sm border-l-4 p-6 hover:shadow-lg transition-all duration-200 ${
                alert.severity === 'critical' ? 'border-l-red-500' :
                alert.severity === 'high' ? 'border-l-orange-500' :
                alert.severity === 'medium' ? 'border-l-yellow-500' :
                'border-l-blue-500'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className={`p-3 rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-100' :
                    alert.severity === 'high' ? 'bg-orange-100' :
                    alert.severity === 'medium' ? 'bg-yellow-100' :
                    'bg-blue-100'
                  }`}>
                    {getAlertIcon(alert.type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{alert.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                        {getSeverityLabel(alert.severity)}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                        {getTypeLabel(alert.type)}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-2">{alert.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{alert.project_name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span>Porteur: {alert.owner_name}</span>
                      </div>
                      {alert.due_date && (
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>Échéance: {format(new Date(alert.due_date), 'dd/MM/yyyy')}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 mb-1">Action recommandée :</p>
                      <p className="text-sm text-gray-700">{alert.action_required}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleAlertAction(alert)}
                    className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <span>Traiter</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                    title="Ignorer"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Paramètres des alertes</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alerte échéance (jours avant)
                </label>
                <input
                  type="number"
                  value={settings.deadline_warning_days}
                  onChange={(e) => setSettings(prev => ({ ...prev, deadline_warning_days: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="30"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alerte inactivité (jours sans mise à jour)
                </label>
                <input
                  type="number"
                  value={settings.inactivity_warning_days}
                  onChange={(e) => setSettings(prev => ({ ...prev, inactivity_warning_days: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="60"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="email_alerts"
                    checked={settings.enable_email_alerts}
                    onChange={(e) => setSettings(prev => ({ ...prev, enable_email_alerts: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="email_alerts" className="text-sm text-gray-700">
                    Recevoir les alertes par email
                  </label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="push_notifications"
                    checked={settings.enable_push_notifications}
                    onChange={(e) => setSettings(prev => ({ ...prev, enable_push_notifications: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="push_notifications" className="text-sm text-gray-700">
                    Notifications push dans le navigateur
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fréquence des alertes
                </label>
                <select
                  value={settings.alert_frequency}
                  onChange={(e) => setSettings(prev => ({ ...prev, alert_frequency: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="immediate">Immédiate</option>
                  <option value="daily">Quotidienne</option>
                  <option value="weekly">Hebdomadaire</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    // Save settings
                    setShowSettings(false);
                    generateAlerts();
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};